// @ts-nocheck
import { useState, useEffect } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Skeleton } from './ui/skeleton';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  ArrowLeft,
  DollarSign,
  Tag,
  Building,
  ExternalLink,
  Edit,
  Link2,
  MessageSquare,
  Eye,
  Save,
  X,
  Zap,
  Target,
  Layers,
  ChevronDown,
  Merge,
  ChevronRight,
  FileText,
  Plus,
  Trash2
} from 'lucide-react';
import { supabase } from '@product-radar/shared';
import { useWorkspace, useRegionalSettings } from '@/contexts/context';
import { toast } from 'sonner';
import { LinkFeedbackIdeaDialog } from './link-feedback-idea-dialog';
import { LinkClientIdeaDialog } from './link-client-idea-dialog';
import { LinkProductAreasDialog } from './link-product-areas-dialog';
import { SendToJiraDialog } from './send-to-jira-dialog';
import { LinkJiraTicketDialog } from './link-jira-ticket-dialog';
import { AuditLog } from './audit-log';
import { formatCurrency, formatDate, formatDateTime } from '@/lib/formatting';

interface IdeaDetailProps {
  ideaId: string;
  onBack: () => void;
  onNavigateToFeedback?: (feedbackId: string) => void;
  onNavigateToClient?: (clientId: string) => void;
}

export function IdeaDetail({ ideaId, onBack, onNavigateToFeedback, onNavigateToClient }: IdeaDetailProps) {
  const { workspace, membership } = useWorkspace();
  const regionalSettings = useRegionalSettings();
  const [idea, setIdea] = useState<any>(null);
  const [linkedFeedback, setLinkedFeedback] = useState<any[]>([]);
  const [linkedClients, setLinkedClients] = useState<any[]>([]);
  const [productAreas, setProductAreas] = useState<any[]>([]);
  const [totalClientValue, setTotalClientValue] = useState<number>(0);
  const [mergedFrom, setMergedFrom] = useState<any[]>([]);
  const [mergedInto, setMergedInto] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkClientsDialogOpen, setLinkClientsDialogOpen] = useState(false);
  const [productAreasDialogOpen, setProductAreasDialogOpen] = useState(false);
  const [linkedJira, setLinkedJira] = useState<any[]>([]);
  const [jiraConnected, setJiraConnected] = useState(false);
  const [jiraInstanceUrl, setJiraInstanceUrl] = useState('');
  const [sendToJiraOpen, setSendToJiraOpen] = useState(false);
  const [linkJiraOpen, setLinkJiraOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    status: 'Backlog',
    priority: 3 as 1 | 2 | 3 | 4 | 5,
    effort: 3 as 1 | 2 | 3 | 4 | 5,
    impact: 3 as 1 | 2 | 3 | 4 | 5,
    reach: null as number | null,
    confidence: null as number | null,
    time_criticality: null as number | null,
    risk_reduction: null as number | null
  });

  useEffect(() => {
    if (workspace && ideaId) {
      loadIdea();
    }
  }, [workspace, ideaId]);

  const loadIdea = async () => {
    if (!workspace) return;

    try {
      setLoading(true);

      // Load idea with tags
      const { data: ideaData, error: ideaError } = await supabase
        .from('ideas')
        .select(`
          *,
          idea_tags(tag:tags(name))
        `)
        .eq('id', ideaId)
        .eq('workspace_id', workspace.id)
        .single();

      if (ideaError) throw ideaError;

      setIdea(ideaData);
      setEditForm({
        title: ideaData.title || '',
        description: ideaData.description || '',
        status: ideaData.status || 'Backlog',
        priority: ideaData.priority || 3,
        effort: ideaData.effort || 3,
        impact: ideaData.impact || 3,
        reach: ideaData.reach ?? null,
        confidence: ideaData.confidence ?? null,
        time_criticality: ideaData.time_criticality ?? null,
        risk_reduction: ideaData.risk_reduction ?? null
      });

      // Load linked feedback
      const { data: feedbackLinks, error: feedbackError } = await supabase
        .from('feedback_ideas')
        .select(`
          feedback:feedback_id(
            id,
            content,
            sentiment,
            source,
            contributor,
            created_at,
            client_id,
            client:client_id(id, name, arr)
          )
        `)
        .eq('idea_id', ideaId);

      if (feedbackError) {
        console.error('Error loading linked feedback:', feedbackError);
      }
      
      if (!feedbackError && feedbackLinks) {
        const feedbackList = feedbackLinks.map((fl: any) => fl.feedback).filter(Boolean);
        setLinkedFeedback(feedbackList);
        
        // Calculate total client value from unique clients in linked feedback
        const clientsMap = new Map();
        let totalValue = 0;
        
        feedbackList.forEach((feedback: any) => {
          if (feedback.client && feedback.client.id) {
            if (!clientsMap.has(feedback.client.id)) {
              clientsMap.set(feedback.client.id, feedback.client);
              // Add client's ARR to total value
              if (feedback.client.arr) {
                totalValue += parseFloat(feedback.client.arr) || 0;
              }
            }
          }
        });
        
        setTotalClientValue(totalValue);
      }

      // Load linked clients
      const { data: clientLinks, error: clientError } = await supabase
        .from('client_ideas')
        .select(`
          client:client_id(
            id,
            name
          ),
          priority
        `)
        .eq('idea_id', ideaId);

      if (!clientError && clientLinks) {
        setLinkedClients(clientLinks.map((cl: any) => ({ ...cl.client, request_priority: cl.priority })).filter(Boolean));
      }

      // Load Jira links
      const { data: jiraLinks } = await supabase
        .from('idea_jira_links')
        .select('*')
        .eq('idea_id', ideaId)
        .order('created_at', { ascending: false });
      setLinkedJira(jiraLinks ?? []);

      // Load Jira connection status
      const { data: jiraStatus } = await supabase.rpc('get_jira_connection_status', { p_workspace_id: workspace.id });
      const jiraRow = Array.isArray(jiraStatus) ? jiraStatus[0] : jiraStatus;
      setJiraConnected(!!jiraRow?.connected);
      setJiraInstanceUrl(jiraRow?.instance_url ?? '');

      // Load product areas (both structure items and products)
      const { data: productAreaData, error: productAreaError } = await supabase
        .from('idea_product_mapping')
        .select(`
          product_item_id,
          product_id,
          product_item:product_structure_items(id, name, type),
          product:products(id, name, color)
        `)
        .eq('idea_id', ideaId);

      if (!productAreaError && productAreaData) {
        const areas = [
          ...productAreaData.filter((item: any) => item.product_item).map((item: any) => item.product_item),
          ...productAreaData.filter((item: any) => item.product).map((item: any) => ({ ...item.product, type: 'product' }))
        ];
        setProductAreas(areas);
      }

      // Load merge history: ideas merged INTO this one
      const { data: mergedFromData } = await supabase
        .from('idea_merges')
        .select('id, source_idea_id, source_title, source_description, merged_at')
        .eq('target_idea_id', ideaId)
        .order('merged_at', { ascending: false });

      if (mergedFromData) {
        setMergedFrom(mergedFromData);
      }

      // Load merge history: was this idea merged INTO another?
      const { data: mergedIntoData } = await supabase
        .from('idea_merges')
        .select('id, target_idea_id, source_title, merged_at, target_idea:ideas!idea_merges_target_idea_id_fkey(id, title)')
        .eq('source_idea_id', ideaId)
        .limit(1)
        .maybeSingle();

      if (mergedIntoData) {
        setMergedInto(mergedIntoData);
      } else {
        setMergedInto(null);
      }

    } catch (error: any) {
      console.error('Error loading idea:', error);
      toast.error('Failed to load idea details', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Reset form to original values
    if (idea) {
      setEditForm({
        title: idea.title || '',
        description: idea.description || '',
        status: idea.status || 'Backlog',
        priority: idea.priority || 3,
        effort: idea.effort || 3,
        impact: idea.impact || 3,
        reach: idea.reach ?? null,
        confidence: idea.confidence ?? null,
        time_criticality: idea.time_criticality ?? null,
        risk_reduction: idea.risk_reduction ?? null
      });
    }
  };

  const handleSaveChanges = async () => {
    if (!idea) return;

    try {
      const { error } = await supabase
        .from('ideas')
        .update({
          title: editForm.title,
          description: editForm.description,
          status: editForm.status,
          priority: editForm.priority,
          effort: editForm.effort,
          impact: editForm.impact,
          reach: editForm.reach,
          confidence: editForm.confidence,
          time_criticality: editForm.time_criticality,
          risk_reduction: editForm.risk_reduction,
          updated_at: new Date().toISOString(),
        })
        .eq('id', idea.id);

      if (error) throw error;

      toast.success('Idea updated successfully');
      setIsEditing(false);
      loadIdea();
    } catch (error: any) {
      console.error('Error updating idea:', error);
      toast.error('Failed to update idea', {
        description: error.message
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'New': return 'secondary';
      case 'Reviewed': return 'default';
      case 'In Prioritization': return 'default';
      case 'Selected': return 'default';
      case 'In Development': return 'default';
      case 'Shipped': return 'default';
      case 'Archived': return 'destructive';
      case 'Merged': return 'secondary';
      default: return 'secondary';
    }
  };

  const getScoreColor = (score: number) => {
    const colors = {
      1: 'bg-gray-400',
      2: 'bg-blue-500',
      3: 'bg-yellow-500',
      4: 'bg-orange-500',
      5: 'bg-red-500'
    };
    return colors[score as keyof typeof colors] || colors[3];
  };

  const ScoreDots = ({ score }: { score: number }) => {
    return (
      <div className="flex items-center gap-2 py-1">
        {[1, 2, 3, 4, 5].map((dot) => (
          <div
            key={dot}
            className={`w-2 h-2 rounded-full ${
              dot <= score ? getScoreColor(score) : 'bg-gray-200'
            }`}
          />
        ))}
        <span className="font-medium">{score}/5</span>
      </div>
    );
  };

  const getSentimentBadge = (sentiment: string) => {
    const styles = {
      'positive': 'bg-green-50 text-green-600 border-green-200',
      'negative': 'bg-red-50 text-red-600 border-red-200',
      'neutral': 'bg-blue-50 text-blue-600 border-blue-200'
    };
    return styles[sentiment as keyof typeof styles] || 'bg-gray-50 text-gray-600 border-gray-200';
  };

  // Check if user has permission to edit
  const canEdit = membership?.role && ['admin', 'product_manager', 'contributor'].includes(membership.role);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-1">
            <Skeleton className="h-96" />
          </div>
          <div className="col-span-2">
            <Skeleton className="h-96" />
          </div>
        </div>
      </div>
    );
  }

  if (!idea) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <p className="text-muted-foreground mb-4">Idea not found</p>
        <Button onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Ideas
        </Button>
      </div>
    );
  }

  const tags = idea.idea_tags?.map((it: any) => it.tag?.name).filter(Boolean) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between border-b pb-4">
        <div className="flex items-center space-x-3 flex-1">
          <Button variant="ghost" size="sm" onClick={onBack} className="h-9">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="h-8 w-px bg-border" />
          <div className="flex-1">
            {isEditing ? (
              <Input 
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                className="text-2xl h-12 border-blue-200 focus:border-blue-400"
                placeholder="Enter idea title..."
              />
            ) : (
              <div className="flex items-center space-x-2">
                <h1 className="text-2xl">{idea.title}</h1>
                <span className="text-sm text-muted-foreground">· ID: {idea.id.substring(0, 8)}</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveChanges}>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </>
          ) : (
            <>
              {canEdit && (
                <Button variant="outline" size="sm" onClick={handleEditClick}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setLinkDialogOpen(true)}>
                <MessageSquare className="h-4 w-4 mr-2" />
                Link Feedback
              </Button>
              {jiraConnected && linkedJira.length > 0 && (
                <Button variant="outline" size="sm" onClick={() => window.open(linkedJira[0].jira_url || `${(jiraInstanceUrl || 'https://example.atlassian.net').replace(/\/$/, '')}/browse/${linkedJira[0].jira_key}`, '_blank')}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Jira
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Edit Mode Banner */}
      {isEditing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Edit className="h-4 w-4 text-blue-600" />
              <p className="text-sm text-blue-900">You are now editing this idea. Make your changes and click "Save Changes" when done.</p>
            </div>
          </div>
        </div>
      )}

      {/* Merged Into Banner */}
      {mergedInto && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Merge className="h-4 w-4 text-amber-600" />
              <p className="text-sm text-amber-900">
                This idea was merged into <span className="font-medium">{mergedInto.target_idea?.title || 'another idea'}</span>
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="text-amber-700 border-amber-300 hover:bg-amber-100"
              onClick={() => onBack()}
            >
              <ChevronRight className="h-4 w-4 mr-1" />
              View Target Idea
            </Button>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-3 gap-6">
        {/* Left column - Description, Jira, Linked Feedback, Interested Clients */}
        <div className="col-span-2 space-y-6">
          {/* Description */}
          <Card className={`p-6 ${isEditing ? 'border-blue-200 shadow-sm' : ''}`}>
            <h3 className="mb-4">Description</h3>
            {isEditing ? (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Description</Label>
                <Textarea 
                  rows={10}
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full border-blue-200 focus:border-blue-400 resize-none"
                  placeholder="Describe the idea in detail..."
                />
                <p className="text-xs text-muted-foreground">
                  {editForm.description.length} characters
                </p>
              </div>
            ) : (
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">
                  {idea.description || 'No description provided'}
                </p>
              </div>
            )}
          </Card>

          {/* Jira Integration */}
          {!isEditing && jiraConnected && (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <h3>Jira Integration</h3>
                  <Badge variant="outline" className="text-xs">
                    {linkedJira.length} linked
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setSendToJiraOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Send to Jira
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setLinkJiraOpen(true)}>
                    <Link2 className="h-4 w-4 mr-2" />
                    Link ticket
                  </Button>
                </div>
              </div>

              {linkedJira.length > 0 ? (
                <div className="space-y-2">
                  {linkedJira.map((link: any) => (
                    <div key={link.id} className="flex items-center justify-between p-2 bg-muted rounded hover:bg-muted/70 transition-colors">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <a
                          href={link.jira_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-primary hover:underline"
                        >
                          {link.jira_key}
                        </a>
                        {link.issue_type && (
                          <Badge variant="outline" className="text-xs">{link.issue_type}</Badge>
                        )}
                        {link.jira_status && (
                          <Badge variant="secondary" className="text-xs">{link.jira_status}</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => window.open(link.jira_url, '_blank')}>
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={async () => {
                              const { error } = await supabase.rpc('unlink_idea_jira', { p_link_id: link.id });
                              if (error) toast.error(error.message);
                              else {
                                setLinkedJira((prev) => prev.filter((l) => l.id !== link.id));
                                toast.success('Unlinked');
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 bg-muted/30 rounded-lg border-2 border-dashed">
                  <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                  <h4 className="text-muted-foreground mb-2">No Jira tickets linked</h4>
                  <p className="text-sm text-muted-foreground mb-4">Create a new ticket or link an existing one.</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setSendToJiraOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Send to Jira
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setLinkJiraOpen(true)}>
                      <Link2 className="h-4 w-4 mr-2" />
                      Link ticket
                    </Button>
                  </div>
                </div>
              )}

              <SendToJiraDialog
                open={sendToJiraOpen}
                onOpenChange={setSendToJiraOpen}
                workspaceId={workspace.id}
                ideaId={ideaId}
                hasExistingLinks={linkedJira.length > 0}
                onSuccess={() => loadIdea()}
              />
              <LinkJiraTicketDialog
                open={linkJiraOpen}
                onOpenChange={setLinkJiraOpen}
                workspaceId={workspace.id}
                ideaId={ideaId}
                onSuccess={() => loadIdea()}
              />
            </Card>
          )}

          {/* Linked Feedback */}
          {!isEditing && (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  <h3>Linked Feedback</h3>
                  <Badge variant="outline" className="text-xs">
                    {linkedFeedback.length} linked
                  </Badge>
                </div>
                <Button variant="outline" size="sm" onClick={() => setLinkDialogOpen(true)}>
                  <Link2 className="h-4 w-4 mr-2" />
                  Link Feedback
                </Button>
              </div>

              {linkedFeedback.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b">
                      <tr>
                        <th className="text-left p-3 text-xs uppercase text-muted-foreground">Feedback</th>
                        <th className="text-left p-3 text-xs uppercase text-muted-foreground">Customer</th>
                        <th className="text-left p-3 text-xs uppercase text-muted-foreground">Source</th>
                        <th className="text-left p-3 text-xs uppercase text-muted-foreground">Sentiment</th>
                        <th className="text-left p-3 text-xs uppercase text-muted-foreground">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linkedFeedback.map((feedback) => (
                        <tr key={feedback.id} className="border-b hover:bg-muted/50 transition-colors">
                          <td className="p-3">
                            <p 
                              className="text-sm text-primary hover:underline cursor-pointer line-clamp-2 max-w-md"
                              onClick={() => onNavigateToFeedback && onNavigateToFeedback(feedback.id)}
                            >
                              {feedback.content}
                            </p>
                          </td>
                          <td className="p-3">
                            <div className="space-y-1">
                              <div className="flex items-center space-x-2">
                                <Building className="h-4 w-4 text-muted-foreground" />
                                {feedback.client && onNavigateToClient ? (
                                  <button
                                    onClick={() => onNavigateToClient(feedback.client.id)}
                                    className="font-medium text-sm text-primary hover:underline"
                                  >
                                    {feedback.client.name}
                                  </button>
                                ) : (
                                  <span className="font-medium text-sm">{feedback.client?.name || 'Unknown'}</span>
                                )}
                              </div>
                              {feedback.author && (
                                <div className="text-xs text-muted-foreground">{feedback.author}</div>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline" className="text-xs">{feedback.source}</Badge>
                          </td>
                          <td className="p-3">
                            {feedback.sentiment && (
                              <Badge className={`${getSentimentBadge(feedback.sentiment)} text-xs`}>
                                {feedback.sentiment}
                              </Badge>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="text-sm text-muted-foreground whitespace-nowrap">
                              {formatDate(feedback.created_at, regionalSettings)}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 bg-muted/30 rounded-lg border-2 border-dashed">
                  <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
                  <h4 className="text-muted-foreground mb-2">No linked feedback yet</h4>
                  <p className="text-sm text-muted-foreground mb-4">Link customer feedback to this idea to track its origin and support.</p>
                  <Button variant="outline" size="sm" onClick={() => setLinkDialogOpen(true)}>
                    <Link2 className="h-4 w-4 mr-2" />
                    Link Feedback
                  </Button>
                </div>
              )}
            </Card>
          )}

          {/* Interested Clients */}
          {!isEditing && (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Building className="h-4 w-4 text-muted-foreground" />
                  <h3>Interested Clients</h3>
                  <Badge variant="outline" className="text-xs">
                    {linkedClients.length} linked
                  </Badge>
                </div>
                <Button variant="outline" size="sm" onClick={() => setLinkClientsDialogOpen(true)}>
                  <Link2 className="h-4 w-4 mr-2" />
                  Link Clients
                </Button>
              </div>

              {linkedClients.length > 0 ? (
                <div className="space-y-2">
                  {linkedClients.map((client: any) => (
                    <div key={client.id} className="flex items-center justify-between p-2 bg-muted rounded hover:bg-muted/70 transition-colors">
                      <div className="flex items-center space-x-2">
                        <Building className="h-4 w-4 text-muted-foreground" />
                        {onNavigateToClient ? (
                          <button
                            onClick={() => onNavigateToClient(client.id)}
                            className="text-sm text-primary hover:underline"
                          >
                            {client.name}
                          </button>
                        ) : (
                          <span className="text-sm">{client.name}</span>
                        )}
                      </div>
                      {client.tier && (
                        <Badge variant="outline" className="text-xs">{client.tier}</Badge>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 bg-muted/30 rounded-lg border-2 border-dashed">
                  <Building className="h-12 w-12 text-muted-foreground mb-4" />
                  <h4 className="text-muted-foreground mb-2">No interested clients linked</h4>
                  <p className="text-sm text-muted-foreground mb-4">Link clients who have expressed interest in this idea.</p>
                  <Button variant="outline" size="sm" onClick={() => setLinkClientsDialogOpen(true)}>
                    <Link2 className="h-4 w-4 mr-2" />
                    Link Clients
                  </Button>
                </div>
              )}
            </Card>
          )}
        </div>

        {/* Right column - Overview, Product Areas, Activity */}
        <div className="col-span-1 space-y-6">
          <Card className={`p-6 ${isEditing ? 'border-blue-200 shadow-sm' : ''}`}>
            <div className="space-y-4">
              {/* Status - first line */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Status</Label>
                {isEditing ? (
                  <Select value={editForm.status} onValueChange={(value) => setEditForm({ ...editForm, status: value })}>
                    <SelectTrigger className="border-blue-200 focus:border-blue-400 h-8">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New">New</SelectItem>
                      <SelectItem value="Reviewed">Reviewed</SelectItem>
                      <SelectItem value="In Prioritization">In Prioritization</SelectItem>
                      <SelectItem value="Selected">Selected</SelectItem>
                      <SelectItem value="In Development">In Development</SelectItem>
                      <SelectItem value="Shipped">Shipped</SelectItem>
                      <SelectItem value="Archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge variant={getStatusColor(idea.status) as any} className="font-normal">
                    {idea.status}
                  </Badge>
                )}
              </div>

              {/* Priority, Effort, Impact - second line */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Priority</Label>
                  {idea.priority && !isEditing ? (
                    <div className="text-sm"><ScoreDots score={idea.priority} /></div>
                  ) : isEditing ? (
                    <Select value={String(editForm.priority)} onValueChange={(value) => setEditForm({ ...editForm, priority: Number(value) as 1 | 2 | 3 | 4 | 5 })}>
                      <SelectTrigger className="border-blue-200 focus:border-blue-400 h-8">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 - Lowest</SelectItem>
                        <SelectItem value="2">2 - Low</SelectItem>
                        <SelectItem value="3">3 - Medium</SelectItem>
                        <SelectItem value="4">4 - High</SelectItem>
                        <SelectItem value="5">5 - Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground flex items-center gap-1">
                    <Zap className="h-3 w-3" />
                    Effort
                  </Label>
                  {idea.effort && !isEditing ? (
                    <div className="text-sm"><ScoreDots score={idea.effort} /></div>
                  ) : isEditing ? (
                    <Select value={String(editForm.effort)} onValueChange={(value) => setEditForm({ ...editForm, effort: Number(value) as 1 | 2 | 3 | 4 | 5 })}>
                      <SelectTrigger className="border-blue-200 focus:border-blue-400 h-8">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 - Very Low</SelectItem>
                        <SelectItem value="2">2 - Low</SelectItem>
                        <SelectItem value="3">3 - Medium</SelectItem>
                        <SelectItem value="4">4 - High</SelectItem>
                        <SelectItem value="5">5 - Very High</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground flex items-center gap-1">
                    <Target className="h-3 w-3" />
                    Impact
                  </Label>
                  {idea.impact && !isEditing ? (
                    <div className="text-sm"><ScoreDots score={idea.impact} /></div>
                  ) : isEditing ? (
                    <Select value={String(editForm.impact)} onValueChange={(value) => setEditForm({ ...editForm, impact: Number(value) as 1 | 2 | 3 | 4 | 5 })}>
                      <SelectTrigger className="border-blue-200 focus:border-blue-400 h-8">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 - Very Low</SelectItem>
                        <SelectItem value="2">2 - Low</SelectItem>
                        <SelectItem value="3">3 - Medium</SelectItem>
                        <SelectItem value="4">4 - High</SelectItem>
                        <SelectItem value="5">5 - Very High</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </div>
              </div>

              {/* Reach */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Reach</Label>
                {isEditing ? (
                  <Input
                    type="number"
                    min="0"
                    placeholder="Linked clients count used if empty"
                    value={editForm.reach ?? ''}
                    onChange={(e) => setEditForm({ ...editForm, reach: e.target.value ? Number(e.target.value) : null })}
                    className="border-blue-200 focus:border-blue-400"
                  />
                ) : (
                  <div className="py-1">
                    <span className="font-medium">{idea.reach != null ? idea.reach : linkedClients.length} <span className="text-muted-foreground font-normal">(users/quarter)</span></span>
                  </div>
                )}
              </div>

              {/* Confidence */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Confidence</Label>
                {isEditing ? (
                  <Select value={editForm.confidence != null ? String(editForm.confidence) : ''} onValueChange={(value) => setEditForm({ ...editForm, confidence: value ? Number(value) : null })}>
                    <SelectTrigger className="border-blue-200 focus:border-blue-400">
                      <SelectValue placeholder="Select confidence" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="50">50%</SelectItem>
                      <SelectItem value="70">70%</SelectItem>
                      <SelectItem value="85">85%</SelectItem>
                      <SelectItem value="100">100%</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="py-1">
                    <span className="font-medium">{idea.confidence != null ? `${idea.confidence}%` : '—'}</span>
                  </div>
                )}
              </div>

              {/* Time Criticality */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Time Criticality</Label>
                {isEditing ? (
                  <Select value={editForm.time_criticality != null ? String(editForm.time_criticality) : ''} onValueChange={(value) => setEditForm({ ...editForm, time_criticality: value ? Number(value) : null })}>
                    <SelectTrigger className="border-blue-200 focus:border-blue-400">
                      <SelectValue placeholder="Select (1-10)" />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="py-1">
                    <span className="font-medium">{idea.time_criticality != null ? idea.time_criticality : '—'}</span>
                  </div>
                )}
              </div>

              {/* Risk Reduction */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Risk Reduction</Label>
                {isEditing ? (
                  <Select value={editForm.risk_reduction != null ? String(editForm.risk_reduction) : ''} onValueChange={(value) => setEditForm({ ...editForm, risk_reduction: value ? Number(value) : null })}>
                    <SelectTrigger className="border-blue-200 focus:border-blue-400">
                      <SelectValue placeholder="Select (1-10)" />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="py-1">
                    <span className="font-medium">{idea.risk_reduction != null ? idea.risk_reduction : '—'}</span>
                  </div>
                )}
              </div>

              {/* Total Client Value */}
              {totalClientValue > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Total Client Value (ARR)</Label>
                  <div className="flex items-center space-x-2 py-1">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-green-600">
                      {formatCurrency(totalClientValue, regionalSettings)}
                    </span>
                  </div>
                </div>
              )}

              {/* Tags */}
              {tags.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Tags</Label>
                  <div className="flex flex-wrap gap-2 py-1">
                    {tags.map((tag: string) => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        <Tag className="h-3 w-3 mr-1" />
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Product Areas */}
          {!isEditing && (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Layers className="h-4 w-4 text-muted-foreground" />
                  <h3>Product Areas</h3>
                  <Badge variant="outline" className="text-xs">
                    {productAreas.length} linked
                  </Badge>
                </div>
                {canEdit && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setProductAreasDialogOpen(true)}
                  >
                    <Link2 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                )}
              </div>
              {productAreas.length > 0 ? (
                <div className="space-y-2">
                  {productAreas.map((area: any) => (
                    <div key={area.id} className="flex items-center justify-between p-2 bg-muted rounded hover:bg-muted/70 transition-colors">
                      <span className="text-sm">{area.name}</span>
                      <Badge variant="outline" className="text-xs">{area.type}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Layers className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No product areas linked</p>
                  {canEdit && (
                    <p className="text-xs mt-1">Click "Add" to link product areas</p>
                  )}
                </div>
              )}
            </Card>
          )}

          {/* Merged Ideas */}
          {!isEditing && mergedFrom.length > 0 && (
            <Card className="p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Merge className="h-4 w-4 text-muted-foreground" />
                <h3>Merged Ideas</h3>
                <Badge variant="outline" className="text-xs">{mergedFrom.length}</Badge>
              </div>
              <div className="space-y-3">
                {mergedFrom.map((merge: any) => (
                  <div key={merge.id} className="p-3 bg-muted/50 rounded-lg border">
                    <div className="font-medium text-sm">{merge.source_title}</div>
                    {merge.source_description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {merge.source_description}
                      </p>
                    )}
                    <div className="text-[10px] text-muted-foreground mt-2">
                      Merged {new Date(merge.merged_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Activity Log */}
          <AuditLog
            recordId={ideaId}
            tableName="ideas"
          />
        </div>
      </div>

      {/* Link Idea to Feedback Dialog */}
      <LinkFeedbackIdeaDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        mode="idea-to-feedback"
        sourceId={ideaId}
        onSuccess={loadIdea}
      />

      {/* Link Clients to Idea Dialog */}
      <LinkClientIdeaDialog
        open={linkClientsDialogOpen}
        onOpenChange={setLinkClientsDialogOpen}
        ideaId={ideaId}
        onSuccess={loadIdea}
      />

      {/* Link Idea to Product Areas Dialog */}
      <LinkProductAreasDialog
        open={productAreasDialogOpen}
        onOpenChange={setProductAreasDialogOpen}
        entityId={ideaId}
        entityType="idea"
        onSuccess={loadIdea}
      />
    </div>
  );
}